import { countryCodes } from "@/Constants";
import { sendVerification, verify } from "@/services/auth";
import { getCountryCode } from "@/services/location";
import { formatE164 } from "@/services/phone";
import { Lock, Send } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import NewUserContainer from "./NewUserContainer";
import PageHeader from "./PageHeader";

export default function LoginContainer() {
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("+1"); // default to US [+1
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");

  // capture the current url and save to local storage
  useEffect(() => {
    const url = new URL(window.location.href);
    localStorage.setItem("redirect", url.pathname);
  }, []);

  // detect the visitor's country and default the calling code (e.g. UK -> +44)
  useEffect(() => {
    const detect = async () => {
      const detected = await getCountryCode();
      if (detected && countryCodes.includes(detected)) {
        setCountryCode(detected);
      }
    };
    detect();
  }, []);

  const formattedPhone = () => {
    return formatE164(countryCode, phone).e164;
  };

  const handleSendVerification = async () => {
    setError("");
    setLoading(true);
    try {
      const response = await sendVerification(formattedPhone());
      if (!response || response.ok === false || response.error) {
        setError(response?.error || "Couldn't send code. Check the number.");
        return;
      }
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentNumber = () => {
    setSent(false);
    setCode("");
    setError("");
  };

  const handleVerify = async (code) => {
    setError("");
    setLoading(true);
    try {
      const response = await verify(formattedPhone(), code);
      // verify() returns `user` for an existing account (and already redirects),
      // or `verified: true` with no user when the phone is confirmed but needs
      // to register. Only a genuinely failed check has neither.
      if (!response || (!response.user && !response.verified)) {
        setError(response?.error || "Invalid code. Please try again.");
        return;
      }
      // add 500ms
      await new Promise((resolve) => setTimeout(resolve, 500));
      setVerified(true);
    } finally {
      setLoading(false);
    }
  };

  const validLength = () => {
    return formatE164(countryCode, phone).isValid;
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setError("");
    setCode(value);
    if (value.length === 6) {
      handleVerify(value);
    }
  };

  return (
    <Container maxWidth="xs">
      <PageHeader compact={verified} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress sx={{ my: 5 }} />
        </Box>
      )}

      {!sent && !loading && (
        <Box>
          <Box style={{ display: "flex", justifyContent: "space-between" }}>
            <Autocomplete
              id="country-code"
              value={countryCode}
              onChange={(e, newValue) => setCountryCode(newValue)}
              options={countryCodes}
              disableClearable
              sx={{ mr: 1, width: "120px" }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
            />
            <TextField
              label="Phone Number"
              variant="outlined"
              type="telephone"
              value={phone}
              onChange={(e) => {
                setError("");
                setPhone(e.target.value.replace(/[^0-9]/g, ""));
              }}
              fullWidth
              error={Boolean(error)}
              helperText={error || ""}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && validLength()) {
                  handleSendVerification();
                }
              }}
            />
          </Box>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSendVerification}
            disabled={!validLength()}
            startIcon={<Send />}
            sx={{ my: 2 }}
          >
            Request code
          </Button>
        </Box>
      )}

      {sent && !verified && !loading && (
        <Box >
          <TextField
            label="Code"
            variant="outlined"
            value={code}
            error={Boolean(error)}
            helperText={error || `Code sent to ${formattedPhone()}`}
            onChange={handleCodeChange}
            fullWidth
            autoComplete="off"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleVerify(code);
              }
            }}
          />
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => handleVerify(code)}
            disabled={code.length != 6}
            startIcon={<Lock />}
            sx={{ my: 2 }}
          >
            Confirm
          </Button>
          <Button variant="text" fullWidth onClick={handleUseDifferentNumber} size="small" color="info" >
            Use a different number
          </Button>
        </Box>
      )}

      {verified && !loading && (
        <NewUserContainer
          countryCode={countryCode}
          phone={phone}
          loading={loading}
          setLoading={setLoading}
        />
      )}

      {/* <Box sx={{ display: "flex", justifyContent: "center", mt: 20 }}>
        <ReleaseDate />
      </Box> */}



    </Container>
  );
}
